/**
 * Convert a tenant-relative `/uploads/...` image URL into an absolute
 * URL anchored at the API host so `<img src>` works regardless of
 * which origin the storefront is served from.
 *
 * The backend stores image URLs returned by `POST /api/Image/upload`
 * as tenant-relative paths (e.g.
 * `/uploads/{tenantId}/banner/2026/05/abc.png`). In dev that loads
 * via Vite's `/uploads/*` proxy rule; in prod the web app's IIS has
 * no such rule, so the relative URL 404s on the web origin. Always
 * resolving to the absolute API-host URL avoids that class of bug
 * entirely.
 *
 * Resolution order for the API origin (first match wins):
 *   1. `window.__KSS_CONFIG__.apiOrigin`
 *      — set by `public/runtime-config.js`; editable post-deploy.
 *   2. `window.__KSS_CONFIG__.apiBaseUrl` minus a trailing `/api`.
 *   3. `VITE_API_ORIGIN` — build-time explicit override.
 *   4. `VITE_API_BASE_URL` minus a trailing `/api`.
 *   5. Hard-coded production API host (last-resort fallback so
 *      images still render if the runtime file is missing).
 *
 * Empty / already-absolute / non-`/uploads` values are passed
 * through unchanged.
 */
/**
 * Last-resort fallback when neither runtime config nor build-time env
 * yields an origin. Uses the page's own origin — works whenever the
 * SPA and the API are served from the same host (IIS reverse proxy,
 * Vite dev `/api` proxy). When the API is on a different subdomain,
 * `window.__KSS_CONFIG__.apiBaseUrl` MUST be set in `runtime-config.js`.
 *
 * NO hardcoded tenant subdomain — the bundle stays tenant-agnostic so
 * the same artefact can deploy to any customer's host.
 */
function fallbackOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
}

interface RuntimeConfig {
  apiBaseUrl?: string;
  apiOrigin?: string;
}
function readRuntimeConfig(): RuntimeConfig {
  if (typeof window === 'undefined') return {};
  return (window as unknown as { __KSS_CONFIG__?: RuntimeConfig }).__KSS_CONFIG__ ?? {};
}

function stripApiSuffix(u: string): string {
  return u.replace(/\/api\/?$/, '');
}

function getApiOrigin(): string {
  // 1. Runtime explicit origin.
  const rt = readRuntimeConfig();
  if (rt.apiOrigin && rt.apiOrigin.trim()) return rt.apiOrigin.trim();
  // 2. Runtime base URL → derive origin.
  if (rt.apiBaseUrl && /^https?:\/\//i.test(rt.apiBaseUrl)) {
    return stripApiSuffix(rt.apiBaseUrl.trim());
  }
  // 3 + 4. Build-time env vars.
  try {
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    if (env?.VITE_API_ORIGIN) return env.VITE_API_ORIGIN;
    const base = env?.VITE_API_BASE_URL;
    if (base && /^https?:\/\//i.test(base)) {
      return stripApiSuffix(base);
    }
  } catch {
    /* fall through */
  }
  // 5. Last-resort: same-origin (works under IIS reverse proxy).
  return fallbackOrigin();
}

export function toAbsoluteImageUrl(value: string | null | undefined): string {
  if (!value) return '';
  const v = String(value).trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  if (!v.startsWith('/')) return v;
  return getApiOrigin() + v;
}
